import { useState, useEffect, useRef, useContext } from 'react'
import { Layout } from "../../componentsV1/layout";
import Logo from "../../assets/v1/logo.png";
import { Antibody, Stack, Graph, Target, Validation } from "../../assets/v1/icons";
import css from './gallery.module.scss'
import { AuthContext } from '../../context/auth'
import { getGalleryApi, getProductsWithHandleApi, getProteinFiltersApi } from '../../api'
import { useNavigate, useParams } from 'react-router-dom'
import { useDebounce } from 'use-debounce'
import MethodologyModal from "../../componentsV1/modals/MethodologyModal"
import { ProductList } from '../../componentsV1/ProductList'
import { ProductDetails } from '../../componentsV1/ProductDetails';
import { methodologyShortName } from '../../utils/methodology'
import { Filters } from "./Filters";
import { proteinsList as allProteinList } from '../../utils/proteinName'


const logos = [
    {
        icon: Target,
        text: "Resolution Aware"
    },
    {
        icon: Stack,
        text: "Systematic Progression"
    },
    {
        icon: Validation,
        text: "Biologically Validated"
    },
    {
        icon: Graph,
        text: "Reliable Results"
    }
]

const tabs = [
    {
        label: 'Primary Antibodies',
        value: 'primary'
    },
    {
        label: 'Conjugated Primary Antibodies',
        value: 'primary_conjugate'
    },
    {
        label: 'Secondary Fluorescent Antibodies',
        value: 'alexafluor'
    }
]

const isValidSlug = (slug) => {
    const slugRegex = /^[a-z0-9]+(-[a-z0-9]+)*$/;
    return slugRegex.test(slug);
}

export const Gallery = () => {
    const { setIsLoading, setNotification, setCartData, isLoading, setUpcomingPurchaseModal, setBulkOrderModal, tempDisable, setTempDisable, width } = useContext(AuthContext)
    const { slug, method } = useParams()
    const navigate = useNavigate()
    const dropdownRef = useRef(null)
    const coARef = useRef(null)
    const counter = useRef(0);
    const [openMethodologyModal, setOpenMethodologyModal] = useState(false)
    const [modalName, setModalName] = useState(null)
    const [productType, setProductType] = useState('alexafluor')
    const [images, setImages] = useState([])
    const [currentPage, setCurrentPage] = useState(0);
    const [search, setSearch] = useState('')
    const [proteinsList, setProteinsList] = useState([])
    const [selectedProduct, setSelectedProduct] = useState(null)
    const [openFilter, setOpenFilter] = useState(false)
    const [filters, setFilters] = useState({
        proteinTarget: {
            dropDown: true,
            selectedOptions: [],
        },
        conjugateType: {
            dropDown: true,
            selectedOptions: [],
        },
        dye: {
            dropDown: true,
            selectedOptions: [],
        },
        resolution: {
            dropDown: true,
            selectedOptions: [],
        },
        primaryType: {
            dropDown: true,
            selectedOptions: '',
        },
        primaryVariantType: {
            dropDown: true,
            selectedOptions: '',
        },
        secondaryAntibodies: {
            dropDown: true,
            selectedOptions: [],
        }
    })
    const [tempFilters, setTempFilters] = useState({
        proteinTarget: {
            dropDown: true,
            selectedOptions: [],
        },
        conjugateType: {
            dropDown: true,
            selectedOptions: [],
        },
        dye: {
            dropDown: false,
            selectedOptions: [],
        },
        resolution: {
            dropDown: false,
            selectedOptions: [],
        },
        primaryType: {
            dropDown: true,
            selectedOptions: '',
        },
        primaryVariantType: {
            dropDown: true,
            selectedOptions: '',
        },
        secondaryAntibodies: {
            dropDown: true,
            selectedOptions: '',
        }
    })
    const [filterApplied, setFilterApplied] = useState(false)
    const [debouncedSearch] = useDebounce(search, 500);
    const [openItemPreviewModal, setOpenItemPreviewModal] = useState(false)
    const [viewType, setViewType] = useState("list")
    const [hasMore, setHasMore] = useState(true);
    const [isFetchingMore, setIsFetchingMore] = useState(false);
    const limit = 30


    const resetFilters = () => {
        setFilters({
            proteinTarget: {
                dropDown: filters.proteinTarget.dropDown,
                selectedOptions: [],
            },
            conjugateType: {
                dropDown: filters.conjugateType.dropDown,
                selectedOptions: [],
            },
            dye: {
                dropDown: filters.dye.dropDown,
                selectedOptions: [],
            },
            resolution: {
                dropDown: filters.resolution.dropDown,
                selectedOptions: [],
            },
            primaryType: {
                dropDown: filters.primaryType.dropDown,
                selectedOptions: '',
            },
            primaryVariantType: {
                dropDown: filters.primaryVariantType.dropDown,
                selectedOptions: '',
            },
            secondaryAntibodies: {
                dropDown: filters.secondaryAntibodies.dropDown,
                selectedOptions: '',
            }
        })
        setCurrentPage(0)
    }

    const extractAndCombinePayload = (data, string = false) => {
        let hosts = new Set();
        let speciesReactivities = new Set();
        let chainSpecifications = new Set();
        let clonality = new Set();
        if (data?.length && !string) {
            data.forEach((item) => {
                const parts = item.split('&');
                parts.forEach((part) => {
                    const [key, value] = part.split('=');
                    if (key === 'species_reactivity') {
                        speciesReactivities.add(value);
                    } else if (key === 'chain_specification') {
                        chainSpecifications.add(value);
                    } else if (key === 'host') {
                        hosts.add(value);
                    }
                });
            });
        }

        if (string) {
            const parts = data.split('&');
            parts.forEach((part) => {
                const [key, value] = part.split('=');
                if (key === 'host') {
                    hosts.add(value);
                } else if (key === 'clonality') {
                    clonality.add(value);
                }
            });
        }

        const hostsArray = Array.from(hosts);
        const speciesReactivitiesArray = Array.from(speciesReactivities);
        const chainSpecificationsArray = Array.from(chainSpecifications);
        const clonalityArray = Array.from(clonality);

        const PayloadData = {
            host: hostsArray.join(', '),
            species_reactivity: speciesReactivitiesArray.join(', '),
            chain_specification: chainSpecificationsArray.join(', '),
            clonality: clonalityArray.join(', '),
        };

        return PayloadData;
    };

    const fetchGallery = () => {
        setIsLoading(true)
        const { proteinTarget, conjugateType, dye, resolution, secondaryAntibodies, primaryType, primaryVariantType } = filters
        const selectedProteins = proteinTarget.selectedOptions?.map(item => item).join(",")
        const containsControl = dye.selectedOptions?.includes('Control')
        const selectedDyes = dye.selectedOptions?.filter(item => item !== 'Control').map(item => item).join(",")
        const selectedResolutions = containsControl ? [...resolution.selectedOptions, 'Control'].join(",") : resolution.selectedOptions?.map(item => item).join(",")
        const selectedHost = extractAndCombinePayload([primaryType?.selectedOptions, ...secondaryAntibodies?.selectedOptions])?.host
        const selectedSpecies = extractAndCombinePayload(secondaryAntibodies?.selectedOptions)?.species_reactivity
        const selectedChain = extractAndCombinePayload(secondaryAntibodies?.selectedOptions)?.chain_specification
        const selectedClonality = extractAndCombinePayload(primaryType?.selectedOptions, true)?.clonality
        const selectedProteinVariantType = primaryVariantType.selectedOptions
        const selectedViewType = viewType

        const payload = {
            view: selectedViewType,
            subtitle: selectedProteins,
            type: productType === 'alexafluor' ? 'SA' : productType === 'primary' ? 'PA-MM,PA-MR,PA-RR,PA-RP,PA-RTM' : productType === 'primary_conjugate' ? 'DC' : "",
            ...(productType !== 'primary' && { dyes: selectedDyes }),
            application: productType === 'Western Blotting' ? 'Western Blotting' : productType === 'flow_cytometry' ? 'Flow Cytometry' : selectedResolutions,
            host: selectedHost,
            ...(!['primary', 'primary_conjugate'].includes(productType) && { species_reactivity: selectedSpecies }),
            ...(!['primary', 'primary_conjugate'].includes(productType) && { chain_specification: selectedChain }),
            ...(!['alexafluor'].includes(productType) && { variant: selectedProteinVariantType }),
            ...(!['alexafluor'].includes(productType) && { clonality: selectedClonality })
        };

        const params = Object.keys(payload).length > 0
            ? '&' + Object.entries(payload)
                .map(([key, value]) => `${key}=${value}`)
                .join('&')
            : '';

        getGalleryApi(limit, currentPage * limit, debouncedSearch, params).then(res => {
            if (res?.data?.products) {
                const newProducts = res.data.products?.map((item) => {
                    return {
                        ...item,
                        title: item?.product_name,
                        mid_code: item?.product_mid_code,
                        material: item?.product_material
                    }
                })
                setImages((prev) => {
                    if (currentPage === 0) {
                        return newProducts;
                    }
                    return [...prev, ...newProducts];
                });
                if (newProducts.length < limit) {
                    setHasMore(false);
                }
                setTimeout(() => {
                    setIsLoading(false)
                }, 2500)
            } else {
                setHasMore(false);
            }
        })
    }

    const fetchProductWithSlug = (slug) => {
        setIsLoading(true)
        if (isValidSlug(slug)) {
            getProductsWithHandleApi(slug).then(res => {
                if (res && res.data && res.data?.products?.length) {
                    setSelectedProduct(res?.data?.products[0])
                    const imgs = res.data?.products[0]?.metadata?.images
                    if (method) {
                        const methodName = method?.toLocaleLowerCase()?.replace(/\s/g, '-')
                        const methodologyName = methodologyShortName[methodName]
                        if (imgs && imgs.some(image => image.name === methodologyName ? methodologyName : method)) {
                            setModalName(methodologyName ? methodologyName : method)
                            setOpenMethodologyModal(true)
                        } else {
                            setModalName('')
                            setOpenMethodologyModal(false)
                            navigate('/product-search')
                        }
                    } else {
                        setOpenItemPreviewModal(true)
                    }
                } else {
                    navigate('/product-search')
                }
                setIsLoading(false)
            }).catch(err => {
                setIsLoading(false)
            })
        } else {
            navigate('/product-search')
        }
    }

    const fetchProteinFilters = () => {
        setIsLoading(true)
        getProteinFiltersApi().then(res => {
            if (res && res?.data && res?.data?.proteins_available) {
                setProteinsList(res?.data?.proteins_available)
                setIsLoading(false)
            } else {
                setProteinsList(allProteinList)
                setIsLoading(false)
            }
        }).catch(() => {
            setIsLoading(false)
            setProteinsList(allProteinList)
        })
    }

    const fetchMoreData = () => {
        if (!isLoading && hasMore) {
            setCurrentPage((prev) => prev + 1);
        }
    };

    useEffect(() => {
        fetchGallery()
        counter.current = 0;
    }, [currentPage, debouncedSearch, filters, productType, viewType])

    useEffect(() => {
        if (slug) {
            fetchProductWithSlug(slug)
        }
    }, [slug])

    // useEffect(() => {
    //     if (!selectedProduct && viewType === 'list') {
    //         navigate('/v1/product-search')
    //     }
    // }, [selectedProduct, viewType])

    useEffect(() => {
        setFilterApplied(Object.values(filters).reduce((total, field) => total + field.selectedOptions.length, 0) > 0 ? true : false)
    }, [filters])
    
    useEffect(() => {
        fetchProteinFilters()
    }, [])

    return (
        <Layout>
            <div className={css.heroContainer}>
                <div className={css.heroContent}>
                    <img src={Logo} alt="logo" className={css.heroLogo} />
                    <div className={css.heroText}>
                        <h4>REAGENT PLATFORM</h4>
                        <div className={css.line} />
                        <h1>REAGENTS THAT POWER CONFIDENT <span>BIOLOGICAL DISCOVERY.</span></h1>
                        <p>Explore our portfolio of primary antibodies, direct conjugates, and secondary reagents—engineered for specificity, consistency, and reproducibility</p>
                    </div>
                    <div className={css.logoContainer}>
                        {logos.map((item) => {
                            return (
                                <div className={css.logo}>
                                    <img src={item.icon} alt={item.text} />
                                    <p>{item.text}</p>
                                </div>
                            )
                        })
                        }
                    </div>
                </div>
            </div>
            <div className={`${css.section} ${selectedProduct ? css.showProduct : css.hideProduct}`}>
                <div className={css.selectionContainer}>
                    <div className={css.listContainer}>
                        <div className={css.filtersContainer}>
                            <div className={css.filters}>
                                <div className={css.filterTabs}>
                                    {tabs.map((item) => {
                                        return <button key={item.value} onClick={() => {
                                            if (item.value === productType) return
                                            setProductType(item.value)
                                            resetFilters()
                                        }}
                                            className={`${css.tab} ${productType === item.value ? css.activeTab : ''}`}
                                        >{item.label}</button>
                                    })
                                    }
                                </div>
                                <button className={css.filterBtn} onClick={() => setOpenFilter(true)}>
                                    Filters
                                </button>
                            </div>
                            {filterApplied && <div className={css.filtersAppliedContainer}>
                                {search && <div className={css.filters}>
                                    <span>Search:</span> {search} <i className='fal fa-times' onClick={() => setSearch('')}></i>
                                </div>
                                }
                                {Object.entries(filters).map(([parentName, { selectedOptions }]) => {
                                    const options = Array.isArray(selectedOptions) ? selectedOptions : [selectedOptions].filter(Boolean);
                                    if (options.length > 0) {
                                        return (
                                            <div key={parentName} className={css.filters}>
                                                <span>{parentName === 'dye' ? 'Dye' : parentName === 'proteinTarget' ? 'Protein Target' : parentName === 'conjugateType' ? 'Conjugate Type' : parentName === 'secondaryAntibodies'
                                                    ? 'Secondary Antibodies' : parentName === 'resolution' ? 'Resolution' : parentName === 'primaryType' ? 'Type' : parentName === 'primaryVariantType' && 'Fc'}:</span>
                                                {options.map((option, index) => {
                                                    if (parentName === 'secondaryAntibodies') {
                                                        option = option.replace(/host=/, '');
                                                        option = option.replace(/species_reactivity=/, '');
                                                        option = option.replace(/&chain_specification=/, ' ');
                                                        option = option.replace('&', ' ');
                                                    }
                                                    if (parentName === 'primaryType') {
                                                        option = option.replace(/host=/, '');
                                                        option = option.replace(/clonality=/, '');
                                                        option = option.replace('&', ' ');
                                                    }

                                                    return (
                                                        <>
                                                            {option}
                                                            {parentName === 'dye' && option !== 'Control' && ' nm'}
                                                            {index !== options.length - 1 && '; '}
                                                        </>
                                                    );
                                                })}
                                                <i className='fal fa-times'
                                                    onClick={() => {
                                                        setFilters(prevFilters => ({
                                                            ...prevFilters,
                                                            [parentName]: {
                                                                ...prevFilters[parentName],
                                                                selectedOptions: parentName === 'primaryType' || parentName === 'primaryVariantType' ? '' : [],
                                                            }
                                                        }))
                                                        setCurrentPage(0)
                                                        setTempFilters(prevFilters => ({
                                                            ...prevFilters,
                                                            [parentName]: {
                                                                ...prevFilters[parentName],
                                                                selectedOptions: parentName === 'primaryType' || parentName === 'primaryVariantType' ? '' : [],
                                                            }
                                                        }))
                                                    }}
                                                ></i>
                                            </div>
                                        );
                                    }
                                    return null;
                                })
                                }
                            </div>
                            }
                        </div>
                        <div className={css.divider} />
                        <ProductList products={images} setSelectedProduct={selectedProduct} hasMore={hasMore} fetchMoreData={fetchMoreData} createSlug={true} maxHeight={width > 750 ? '862px' : '500px'} isLoading={isLoading} />
                    </div>
                </div>
                <div className={css.productContainer}>
                    <ProductDetails selectedProduct={selectedProduct} setSelectedProduct={setSelectedProduct} setModalName={setModalName} setOpenMethodologyModal={setOpenMethodologyModal} isSlug={true} />
                </div>
                {openFilter && <Filters setFilters={setFilters} filters={filters} resetFilters={resetFilters} setCurrentPage={setCurrentPage} setOpenFilter={setOpenFilter} setIsLoading={setIsLoading} productType={productType} proteinsList={proteinsList} />}
            </div>
            {openMethodologyModal && <MethodologyModal setOpenMethodologyModal={setOpenMethodologyModal} selectedProduct={selectedProduct} modalName={modalName} setModalName={setModalName} createSlug={true} />}
        </Layout>
    )
}